import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { FeatureKey } from 'common/constants/permission'
import {
  getPondOldestIotDeviceKey,
  getWaterBacteriaKey,
  getWaterMineralKey,
  getWaterQualityKey,
} from 'common/constants/queryKeys'
import useCycleApi from 'common/hooks/useCycleApi'
import { BasePaginationResponseV2, BaseResponse } from 'common/types/api'
import { formatDate } from 'common/utils/date'
import { selectedCycleIdState } from 'modules/pond/stores/atom'
import { useUserInfo } from 'modules/user/api/queries'
import { useParams } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import {
  WATER_MINERAL_REQUEST_CONFIG,
  WATER_QUALITY_REQUEST_CONFIG,
  WATER_VIBRIO_REQUEST_CONFIG,
} from '.'
import { waterDateFilterState, waterDateTableFilterState } from '../stores/atom'
import { getOldestIotDeviceByPond } from './iot'
import { WaterQueryParams } from './selector'
import {
  DisplayMode,
  Vibrio,
  VibrioChart,
  WaterMineral,
  WaterMineralChart,
  WaterQuality,
  WaterQualityChart,
  WaterQualityToday,
} from './types'

const ROWS_PER_PAGE = 10

const toDate = (value?: string | Date | null) => {
  if (!value) return undefined
  return value instanceof Date ? value : new Date(value)
}

const useWaterPermissions = () => {
  const { data: userInfo } = useUserInfo()
  const permissions = userInfo?.user_role.permissions ?? []

  const checkPermit = (key: FeatureKey) => {
    const feature = permissions.find((p) => p.feature.key === key)
    return !!feature?.crud_permission[1]
  }

  return {
    quality: checkPermit(FeatureKey.WaterQuality),
    mineral: checkPermit(FeatureKey.WaterMineral),
    vibrio: checkPermit(FeatureKey.WaterVibrio),
  }
}

export const useGetWaterQualities = (mode: DisplayMode, params?: WaterQueryParams) => {
  const cycleApi = useCycleApi()
  const { quality } = useWaterPermissions()

  const recoilState = mode === 'card' ? waterDateFilterState : waterDateTableFilterState
  const recoilDate = useRecoilValue(recoilState)
  const cycleId = useRecoilValue(selectedCycleIdState)

  const startDate = params?.startDate
    ? formatDate(toDate(params.startDate)!)
    : recoilDate
      ? formatDate(toDate(recoilDate)!)
      : ''

  const endDate = params?.endDate ? formatDate(toDate(params.endDate)!) : ''

  const initParams = {
    params: { pageSize: ROWS_PER_PAGE, page: 1, startDate, endDate },
    cycleId,
  }

  return useInfiniteQuery({
    queryKey: [...getWaterQualityKey.list(cycleId || '', startDate), params],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await cycleApi.request<BasePaginationResponseV2<WaterQuality[]>>({
        ...WATER_QUALITY_REQUEST_CONFIG.get,
        ...pageParam,
      })
      return { water: data.data, nextPage: data.nextPage }
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage
        ? { cycleId, params: { ...initParams.params, page: lastPage.nextPage } }
        : undefined,
    staleTime: Infinity,
    enabled: !!cycleId && quality,
  })
}

export const useGetWaterMinerals = (mode: DisplayMode, params?: WaterQueryParams) => {
  const cycleApi = useCycleApi()
  const { mineral } = useWaterPermissions()

  const recoilState = mode === 'card' ? waterDateFilterState : waterDateTableFilterState
  const recoilDate = useRecoilValue(recoilState)
  const cycleId = useRecoilValue(selectedCycleIdState)

  const startDate = params?.startDate
    ? formatDate(toDate(params.startDate)!)
    : recoilDate
      ? formatDate(toDate(recoilDate)!)
      : ''

  const endDate = params?.endDate ? formatDate(toDate(params.endDate)!) : ''

  const initParams = {
    params: { pageSize: ROWS_PER_PAGE, page: 1, startDate, endDate },
    cycleId,
  }

  return useInfiniteQuery({
    queryKey: [...getWaterMineralKey.list(cycleId || '', startDate), params],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await cycleApi.request<BasePaginationResponseV2<WaterMineral>>({
        ...WATER_MINERAL_REQUEST_CONFIG.get,
        ...pageParam,
      })
      return { minerals: data.data, nextPage: data.nextPage }
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage
        ? { cycleId, params: { ...initParams.params, page: lastPage.nextPage } }
        : undefined,
    staleTime: Infinity,
    enabled: !!cycleId && mineral,
  })
}

export const useGetWaterBacterias = (mode: DisplayMode, params?: WaterQueryParams) => {
  const cycleApi = useCycleApi()
  const { vibrio } = useWaterPermissions()

  const recoilState = mode === 'card' ? waterDateFilterState : waterDateTableFilterState
  const recoilDate = useRecoilValue(recoilState)
  const cycleId = useRecoilValue(selectedCycleIdState)

  const startDate = params?.startDate
    ? formatDate(toDate(params.startDate)!)
    : recoilDate
      ? formatDate(toDate(recoilDate)!)
      : ''

  const endDate = params?.endDate ? formatDate(toDate(params.endDate)!) : ''

  const initParams = {
    params: { pageSize: ROWS_PER_PAGE, page: 1, startDate, endDate },
    cycleId,
  }

  return useInfiniteQuery({
    queryKey: [...getWaterBacteriaKey.list(cycleId || '', startDate), params],
    queryFn: async ({ pageParam = initParams }) => {
      const { data } = await cycleApi.request<BasePaginationResponseV2<Vibrio>>({
        ...WATER_VIBRIO_REQUEST_CONFIG.get,
        ...pageParam,
      })
      return { bacterias: data.data, nextPage: data.nextPage }
    },
    getNextPageParam: (lastPage) =>
      lastPage.nextPage
        ? { cycleId, params: { ...initParams.params, page: lastPage.nextPage } }
        : undefined,
    staleTime: Infinity,
    enabled: !!cycleId && vibrio,
  })
}

export const useGetWaterQualityLineChart = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { quality } = useWaterPermissions()

  return useQuery({
    queryKey: getWaterQualityKey.chart(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<WaterQualityChart[]>>(
        WATER_QUALITY_REQUEST_CONFIG.getLineChart
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId && quality,
  })
}

export const useGetWaterMineralLineChart = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { mineral } = useWaterPermissions()

  return useQuery({
    queryKey: getWaterMineralKey.chart(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<WaterMineralChart[]>>(
        WATER_MINERAL_REQUEST_CONFIG.getLineChart
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId && mineral,
  })
}

export const useGetWaterBacteriaLineChart = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { vibrio } = useWaterPermissions()

  return useQuery({
    queryKey: getWaterBacteriaKey.chart(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<VibrioChart[]>>(
        WATER_VIBRIO_REQUEST_CONFIG.getLineChart
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId && vibrio,
  })
}

const TTL_5_MIN = 5 * 60 * 1000;

export const useGetOldestIotDeviceByPond = () => {
  const { farmId, pondId } = useParams<{ farmId: string; pondId: string }>()

  return useQuery({
    queryKey: getPondOldestIotDeviceKey(farmId ?? '', pondId ?? ''),
    queryFn: async () => {
      const { data } = await getOldestIotDeviceByPond(farmId!, pondId!)
      return data.data
    },
    enabled: !!farmId && !!pondId,
    staleTime: TTL_5_MIN,
    retry: false,
  })
}

export const useGetWaterQualityToday = () => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)
  const { quality } = useWaterPermissions()

  return useQuery({
    queryKey: getWaterQualityKey.today(cycleId || ''),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<WaterQualityToday>>(
        WATER_QUALITY_REQUEST_CONFIG.getToday
      )
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId && quality,
  })
}

export const useGetWaterQualityByDate = (date: Date | null) => {
  const cycleApi = useCycleApi()
  const cycleId = useRecoilValue(selectedCycleIdState)

  return useQuery({
    queryKey: getWaterQualityKey.byDate(cycleId || '', date ? formatDate(date) : undefined),
    queryFn: async () => {
      const { data } = await cycleApi.request<BaseResponse<WaterQualityToday>>({
        ...WATER_QUALITY_REQUEST_CONFIG.getByDate,
        params: { date: formatDate(date!) },
      })
      return data.data
    },
    staleTime: Infinity,
    enabled: !!cycleId && !!date,
  })
}
